import { jsPDF } from 'jspdf';
function getLogoBase64() {
  return new Promise((resolve, reject) => {
    fetch('/Proline Logo HQ.png')
      .then(r => r.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      })
      .catch(reject);
  });
}

const EWN_WORDING = `EARLY WARNING NOTICE\n\nThis document serves as an Early Warning Notice pursuant to the contract conditions. The Contractor hereby gives notice of a matter which could:\n(a) increase the total of the Prices,\n(b) delay Completion or a Key Date,\n(c) delay meeting a Condition for a bonus, or\n(d) impair the performance of the works in use.\n\nThe Subcontractor reserves all rights and entitlements under the subcontract. This notice is issued without prejudice to any other rights and remedies available. The works described herein fall outside the agreed subcontract scope and will require separate assessment and valuation.`;

const CVI_WORDING = `CONFIRMATION OF VERBAL INSTRUCTION\n\nThis document confirms a verbal instruction received on the date and time stated above. The Subcontractor has carried out or will carry out the instructed works under protest, reserving all rights to recover additional costs and extensions of time as appropriate.\n\nThe works described herein constitute a change to the agreed subcontract scope. The Subcontractor hereby notifies the Main Contractor that costs will be tracked and a variation account will be submitted for agreement. All additional resources deployed shall be recorded on contemporaneous daywork sheets.\n\nProline Internals Ltd will not proceed with the instructed works until a formal written instruction has been received unless otherwise agreed in writing.`;

const IR_WORDING = `INSTRUCTION RECEIVED\n\nThis records a formal instruction received from the Main Contractor. The attached document(s) constitute the instruction. Proline Internals Ltd will proceed in accordance with the instruction subject to agreement of any additional costs and/or time.`;

const LEGAL_WORDING = { EWN: EWN_WORDING, CVI: CVI_WORDING, IR: IR_WORDING };

const TYPE_BANNER = {
  EWN: { label: 'EARLY WARNING NOTICE', fill: [217, 119, 6], text: [255, 255, 255] },
  CVI: { label: 'CONFIRMATION OF VERBAL INSTRUCTION', fill: [30, 64, 175], text: [255, 255, 255] },
  IR:  { label: 'INSTRUCTION RECEIVED', fill: [26, 26, 46], text: [255, 255, 255] },
};

const IMPACT_LABELS = {
  cost: 'Cost',
  programme: 'Programme / Time',
  quality: 'Quality',
  health_safety: 'Health & Safety',
  design: 'Design',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function loadLogoImage(dataUrl) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

function triggerBlobDownload(doc, fileName) {
  const blob = doc.output('blob');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function drawTextLogo(doc, margin) {
  doc.setFillColor(26, 26, 46);
  doc.rect(margin, 10, 45, 18, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('PROLINE', margin + 3, 22);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.text('INTERNALS LTD', margin + 3, 27);
  doc.setTextColor(0, 0, 0);
}

export async function generateInstructionPDF({
  instruction,
  projectName,
  projectRef,
  mcName,
  raisedBy,
  attachments,
  skipDownload = false,
}) {
  const { type, reference, status, date, time, created_at } = instruction;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;
  const lh = 6;

  // ── LOGO ────────────────────────────────────────────────────────────────────
  const logoImg = await loadLogoImage(await getLogoBase64());
  if (logoImg) {
    try { doc.addImage(logoImg, 'PNG', margin, 10, 45, 18); }
    catch (_) { drawTextLogo(doc, margin); }
  } else {
    drawTextLogo(doc, margin);
  }

  doc.setFontSize(7);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100);
  doc.text('Proline Internals Ltd', margin, 31);
  doc.setFontSize(6);
  doc.text('Commercial Management System', margin, 35);

  // ── REFERENCE BOX (top right) ─────────────────────────────────────────────
  const boxW = 60;
  const boxX = pageW - margin - boxW;
  const boxY = 10;
  const boxH = 14;

  doc.setFillColor(26, 26, 46);
  doc.rect(boxX, boxY, boxW, boxH, 'F');
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(reference || '—', boxX + boxW / 2, boxY + 9, { align: 'center' });

  doc.setFont(undefined, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(60);
  doc.text(`Date: ${fmtDate(date || created_at)}`, boxX + boxW / 2, boxY + boxH + 5, { align: 'center' });

  // Status badge
  const statusBgMap = {
    draft:                  [240, 240, 240],
    open:                   [219, 234, 254],
    awaiting_instruction:   [255, 251, 235],
    sent_to_mc:             [252, 231, 243],
    ir_raised:              [209, 250, 229],
    confirmed:              [209, 250, 229],
    closed:                 [243, 244, 246],
  };
  const statusTxtMap = {
    draft:                  [80, 80, 80],
    open:                   [30, 64, 175],
    awaiting_instruction:   [146, 64, 14],
    sent_to_mc:             [157, 23, 77],
    ir_raised:              [6, 78, 59],
    confirmed:              [6, 78, 59],
    closed:                 [55, 65, 81],
  };
  const sBg = statusBgMap[status] || [240, 240, 240];
  const sTxt = statusTxtMap[status] || [80, 80, 80];
  const badgeW = 34;
  const badgeX = boxX + (boxW - badgeW) / 2;
  const badgeY = boxY + boxH + 8;
  doc.setFillColor(...sBg);
  doc.roundedRect(badgeX, badgeY, badgeW, 6.5, 1.5, 1.5, 'F');
  doc.setFontSize(6.5);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...sTxt);
  doc.text((status || '').toUpperCase().replace(/_/g, ' '), badgeX + badgeW / 2, badgeY + 4.5, { align: 'center' });

  // ── DIVIDER ──────────────────────────────────────────────────────────────────
  const ruleY = 42;
  doc.setDrawColor(190);
  doc.setLineWidth(0.4);
  doc.line(margin, ruleY, pageW - margin, ruleY);

  // ── DOCUMENT TYPE BANNER ──────────────────────────────────────────────────
  const banner = TYPE_BANNER[type] || TYPE_BANNER.EWN;
  const bannerY = ruleY + 4;
  const bannerH = 9;
  doc.setFillColor(...banner.fill);
  doc.rect(margin, bannerY, contentW, bannerH, 'F');
  doc.setFont(undefined, 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...banner.text);
  doc.text(banner.label, pageW / 2, bannerY + 6, { align: 'center' });

  let nextY = bannerY + bannerH + 8;

  // ── HELPERS ───────────────────────────────────────────────────────────────
  function infoLabel(x, y, label) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(80);
    doc.text(label + ':', x, y);
    return doc.getTextWidth(label + ': ');
  }

  function infoValue(x, y, value, maxW) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    const lines = doc.splitTextToSize(String(value || '—'), maxW);
    doc.text(lines.slice(0, 3), x, y);
    return lh * Math.max(1, Math.min(3, lines.length));
  }

  function sectionHeading(label, y) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(26, 26, 46);
    doc.text(label, margin, y);
  }

  function textBox(text, y) {
    const lines = doc.splitTextToSize(text || '—', contentW - 8);
    const boxH = Math.max(10, lines.length * 4.5 + 6);
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(210);
    doc.setLineWidth(0.3);
    doc.rect(margin, y, contentW, boxH);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(30);
    doc.text(lines, margin + 4, y + 5);
    return y + boxH + 6;
  }

  const col2X = margin + contentW / 2;

  // ── PROJECT INFO ─────────────────────────────────────────────────────────
  {
    const lw = infoLabel(margin, nextY, 'Project');
    infoValue(margin + lw, nextY, projectName || projectRef || '—', contentW - lw);
    nextY += lh;
    const lw2 = infoLabel(margin, nextY, 'Main Contractor');
    infoValue(margin + lw2, nextY, mcName || '—', contentW / 2 - lw2 - 4);
    nextY += lh;
  }

  // ── TYPE-SPECIFIC CONTENT ─────────────────────────────────────────────────

  if (type === 'EWN') {
    // Date / Time / Location
    const lw1 = infoLabel(margin, nextY, 'Date Noticed');
    infoValue(margin + lw1, nextY, fmtDate(date), contentW / 2 - lw1 - 4);
    if (time) {
      const lw2 = infoLabel(col2X, nextY, 'Time Noticed');
      infoValue(col2X + lw2, nextY, String(time).substring(0, 5), contentW / 2 - lw2);
    }
    nextY += lh;

    const locParts = [
      instruction.location && `Location: ${instruction.location}`,
      instruction.level && `Level: ${instruction.level}`,
      instruction.gridlines && `Gridlines: ${instruction.gridlines}`,
    ].filter(Boolean).join('  |  ');
    if (locParts) {
      const lw = infoLabel(margin, nextY, 'Location');
      infoValue(margin + lw, nextY, locParts, contentW - lw);
      nextY += lh;
    }

    // Raised by
    const lw3 = infoLabel(margin, nextY, 'Raised By');
    infoValue(margin + lw3, nextY, raisedBy || '—', contentW / 2 - lw3 - 4);
    nextY += lh + 4;

    // Description
    if (nextY > pageH - 60) { doc.addPage(); nextY = 18; }
    sectionHeading('Description of Issue', nextY);
    nextY += 6;
    nextY = textBox(instruction.description, nextY);

    // Potential impact
    const impacts = (instruction.potential_impact || []).map(v => IMPACT_LABELS[v] || v).filter(Boolean);
    if (impacts.length > 0) {
      if (nextY > pageH - 40) { doc.addPage(); nextY = 18; }
      sectionHeading('Potential Impact', nextY);
      nextY += 6;
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30);
      impacts.forEach(imp => {
        doc.text(`• ${imp}`, margin + 4, nextY);
        nextY += 5;
      });
      nextY += 4;
    }

    // Action requested
    if (instruction.action_requested) {
      if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }
      sectionHeading('Action Requested from Main Contractor', nextY);
      nextY += 6;
      nextY = textBox(instruction.action_requested, nextY);
    }
  }

  if (type === 'CVI') {
    // Verbal instruction details
    const verbalDate = instruction.verbal_date || date;
    const verbalTime = instruction.verbal_time || time;
    const lw1 = infoLabel(margin, nextY, 'Date of Verbal Instruction');
    infoValue(margin + lw1, nextY, fmtDate(verbalDate), contentW / 2 - lw1 - 4);
    if (verbalTime) {
      const lw2 = infoLabel(col2X, nextY, 'Time');
      infoValue(col2X + lw2, nextY, String(verbalTime).substring(0, 5), contentW / 2 - lw2);
    }
    nextY += lh;

    if (instruction.verbal_method) {
      const lw = infoLabel(margin, nextY, 'Method');
      infoValue(margin + lw, nextY, instruction.verbal_method, contentW - lw);
      nextY += lh;
    }

    const byName = instruction.instructed_by_name;
    const byRole = instruction.instructed_by_role;
    if (byName || byRole) {
      const lw = infoLabel(margin, nextY, 'Verbal Instruction Given By');
      infoValue(margin + lw, nextY, [byName, byRole].filter(Boolean).join(' — '), contentW - lw);
      nextY += lh;
    }

    const lw3 = infoLabel(margin, nextY, 'Confirmed By (Proline)');
    infoValue(margin + lw3, nextY, raisedBy || '—', contentW / 2 - lw3 - 4);
    nextY += lh + 4;

    // Description
    if (nextY > pageH - 60) { doc.addPage(); nextY = 18; }
    sectionHeading('Works Verbally Instructed', nextY);
    nextY += 6;
    nextY = textBox(instruction.description, nextY);
  }

  if (type === 'IR') {
    // Instruction details
    const lw1 = infoLabel(margin, nextY, 'Date Received');
    infoValue(margin + lw1, nextY, fmtDate(date), contentW / 2 - lw1 - 4);
    if (time) {
      const lw2 = infoLabel(col2X, nextY, 'Time');
      infoValue(col2X + lw2, nextY, String(time).substring(0, 5), contentW / 2 - lw2);
    }
    nextY += lh;

    if (instruction.instruction_method) {
      const lw = infoLabel(margin, nextY, 'How Received');
      infoValue(margin + lw, nextY, instruction.instruction_method, contentW - lw);
      nextY += lh;
    }

    const byName = instruction.instructed_by_name;
    const byRole = instruction.instructed_by_role;
    if (byName || byRole) {
      const lw = infoLabel(margin, nextY, 'Issued By (MC)');
      infoValue(margin + lw, nextY, [byName, byRole].filter(Boolean).join(' — '), contentW - lw);
      nextY += lh;
    }

    const locParts = [
      instruction.location && `Location: ${instruction.location}`,
      instruction.level && `Level: ${instruction.level}`,
      instruction.gridlines && `Gridlines: ${instruction.gridlines}`,
    ].filter(Boolean).join('  |  ');
    if (locParts) {
      const lw = infoLabel(margin, nextY, 'Location');
      infoValue(margin + lw, nextY, locParts, contentW - lw);
      nextY += lh;
    }

    nextY += 4;

    // Description
    if (nextY > pageH - 60) { doc.addPage(); nextY = 18; }
    sectionHeading('Description of Works Instructed', nextY);
    nextY += 6;
    nextY = textBox(instruction.description, nextY);

    // Reason outside scope
    if (instruction.reason_outside_scope) {
      if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }
      sectionHeading('Reason Works Are Outside Subcontract Scope', nextY);
      nextY += 6;
      nextY = textBox(instruction.reason_outside_scope, nextY);
    }
  }

  // ── ATTACHMENTS LIST ──────────────────────────────────────────────────────
  const attList = attachments || [];
  if (nextY > pageH - 50) { doc.addPage(); nextY = 18; }
  sectionHeading('Attachments', nextY);
  nextY += 6;

  if (attList.length === 0) {
    doc.setFontSize(8);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(120);
    doc.text('No attachments recorded.', margin + 4, nextY + 5);
    nextY += 14;
  } else {
    const attBoxStartY = nextY;
    let ay = nextY + 5;
    attList.forEach((att, idx) => {
      const line = att.file_name || `Attachment ${idx + 1}`;
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(26, 26, 46);
      doc.text('•', margin + 4, ay);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30);
      const aLines = doc.splitTextToSize(line, contentW - 16);
      doc.text(aLines, margin + 8, ay);
      ay += aLines.length * 4.5 + 3;
    });
    ay += 3;
    const attBoxH = ay - attBoxStartY + 2;
    doc.setDrawColor(200);
    doc.setLineWidth(0.3);
    doc.rect(margin, attBoxStartY, contentW, attBoxH);
    nextY = attBoxStartY + attBoxH + 10;
  }

  // ── LEGAL WORDING ─────────────────────────────────────────────────────────
  const legalText = LEGAL_WORDING[type];
  if (legalText) {
    if (nextY > pageH - 70) { doc.addPage(); nextY = 18; }
    const legalBanner = TYPE_BANNER[type] || TYPE_BANNER.EWN;
    doc.setFillColor(...legalBanner.fill);
    doc.rect(margin, nextY, contentW, 7, 'F');
    doc.setFont(undefined, 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...legalBanner.text);
    doc.text('CONTRACTUAL NOTICE', margin + 4, nextY + 5);
    nextY += 9;

    const legalLines = doc.splitTextToSize(legalText, contentW - 8);
    const legalBoxH = legalLines.length * 4 + 8;
    doc.setFillColor(252, 251, 243);
    doc.setDrawColor(217, 119, 6);
    doc.setLineWidth(0.4);
    doc.rect(margin, nextY, contentW, legalBoxH);
    doc.setFont(undefined, 'italic');
    doc.setFontSize(7);
    doc.setTextColor(80, 50, 10);
    doc.text(legalLines, margin + 4, nextY + 5);
    nextY += legalBoxH + 10;
  }

  // ── FOOTER ON EVERY PAGE ─────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  const genDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const fy = pageH - 7;
    doc.setFontSize(6);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(130);
    doc.setDrawColor(200);
    doc.setLineWidth(0.25);
    doc.line(margin, fy - 3, pageW - margin, fy - 3);
    doc.text('Proline Internals Ltd — Confidential', margin, fy);
    doc.text(`Page ${p} of ${totalPages}`, pageW / 2, fy, { align: 'center' });
    doc.text(`Generated: ${genDate}`, pageW - margin, fy, { align: 'right' });
  }

  const fileName = `${reference}_${type}_${(date || '').replace(/-/g, '')}.pdf`;
  const base64 = doc.output('datauristring').split(',')[1];
  if (!skipDownload) triggerBlobDownload(doc, fileName);
  return { fileName, base64 };
}
